import { useForm } from 'react-hook-form'
import { z } from 'zod'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  FormDescription,
} from '@/components/ui/form'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Switch } from '@/components/ui/switch'
import { Input } from '@/components/ui/input'
import { useAuth } from '@/hooks/useAuth'
import { toast } from 'sonner'
import { broadcastDeviceService } from '@/services/broadcast_device_service'
import { BroadcastDeviceVideoType } from '@magnetar/data-access'

export const formSchema = z.object({
  name: z.string().max(100, '名稱最多100字').optional(),
  broadcast_type: z.enum(['tv', 'clinic_vod', 'health_vod']),
  is_active: z.boolean().default(true),
})

interface BroadcastDeviceCreateDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  clinicId: number
  onSuccess?: () => void
}

export function BroadcastDeviceCreateDialog({
  open,
  onOpenChange,
  clinicId,
  onSuccess,
}: BroadcastDeviceCreateDialogProps) {
  const { token } = useAuth()

  const form = useForm<z.infer<typeof formSchema>>({
    // resolver: zodResolver(formSchema),
    defaultValues: {
      name: '',
      broadcast_type: 'tv' as BroadcastDeviceVideoType,
      is_active: true,
    },
  })

  const onSubmit = async (values: z.infer<typeof formSchema>) => {
    if (!token) return

    try {
      const response = await broadcastDeviceService.createBroadcastDevice(
        clinicId,
        {
          name: values.name,
          broadcast_type: values.broadcast_type,
          is_active: values.is_active,
          examination_room_ids: [],
        },
        token,
      )

      if (!response.success) {
        throw new Error(response.error?.message || '新增廣播設備失敗')
      }

      toast.success('新增廣播設備成功')

      form.reset()
      onSuccess?.()
      onOpenChange(false)
    } catch {
      toast.error('新增廣播設備失敗')
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>新增廣播設備</DialogTitle>
          <DialogDescription>新增廣播設備至診所</DialogDescription>
        </DialogHeader>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            <FormField
              control={form.control}
              name="name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>設備名稱</FormLabel>
                  <FormControl>
                    <Input placeholder="請輸入設備名稱" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="broadcast_type"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>廣播類型</FormLabel>
                  <Select onValueChange={field.onChange} defaultValue={field.value}>
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder="選擇廣播類型" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      <SelectItem value="tv">電視</SelectItem>
                      <SelectItem value="clinic_vod">診所影片</SelectItem>
                      <SelectItem value="health_vod">健康影片</SelectItem>
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="is_active"
              render={({ field }) => (
                <FormItem className="flex flex-row items-center justify-between rounded-lg border p-4">
                  <div className="space-y-0.5">
                    <FormLabel className="text-base">啟用狀態</FormLabel>
                    <FormDescription>{field.value ? '啟用' : '停用'}</FormDescription>
                  </div>
                  <FormControl>
                    <Switch checked={field.value} onCheckedChange={field.onChange} />
                  </FormControl>
                </FormItem>
              )}
            />
            <DialogFooter>
              <Button type="submit">新增</Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  )
}
